import React, { useState, useEffect } from 'react';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Heading } from '@/components/Typography';
import Chip from '@/components/Chip';
import LoadingState from '@/components/LoadingState';
import { AuditRecord, AuditHistoryResponse } from '@/types/onboardingBypass';
import { OnboardingBypassAPI } from '@/services/onboardingBypassAPI';
import { format } from 'date-fns';

interface AuditHistoryModalProps {
  open: boolean;
  onClose: () => void;
  workerId?: number;
  workerName?: string;
}

const AuditHistoryModal: React.FC<AuditHistoryModalProps> = ({
  open,
  onClose,
  workerId,
  workerName,
}) => {
  const [auditHistory, setAuditHistory] = useState<AuditRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 20,
  });
  const [filters, setFilters] = useState({
    admin_user_id: '',
    action_type: '',
    date_from: '',
    date_to: '',
  });

  useEffect(() => {
    if (open) {
      fetchAuditHistory(1);
    }
  }, [open, workerId]);

  const fetchAuditHistory = async (page: number = 1) => {
    setLoading(true);
    try {
      let response: { success: boolean; data: AuditHistoryResponse };

      if (workerId) {
        // Fetch audit history for specific worker
        response = await OnboardingBypassAPI.getWorkerAuditTrail(workerId, {
          page,
          per_page: pagination.per_page,
          ...filters,
        });
      } else {
        // Fetch system-wide audit history
        response = await OnboardingBypassAPI.getSystemAuditHistory({
          page,
          per_page: pagination.per_page,
          ...filters,
        });
      }

      if (response.success) {
        setAuditHistory(response.data.audits);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching audit history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
    } catch (e) {
      return dateString;
    }
  };

  const getActionTypeChip = (actionType: string) => {
    switch (actionType) {
      case 'bypass':
        return <Chip color="error" label="Bypass" size="small" />;
      case 'state_change':
        return <Chip color="warning" label="State Change" size="small" />;
      case 'manual_completion':
        return <Chip color="success" label="Manual Completion" size="small" />;
      default:
        return <Chip label={actionType} size="small" />;
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchAuditHistory(newPage);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchAuditHistory(1);
  };

  const clearFilters = () => {
    setFilters({
      admin_user_id: '',
      action_type: '',
      date_from: '',
      date_to: '',
    });
    setTimeout(() => fetchAuditHistory(1), 0);
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '1000px',
          maxHeight: '90vh',
          overflow: 'auto',
          width: '90%',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Stack gap={16} vertical>
          {/* Header */}
          <Stack gap={16} justify="space-between" align="center">
            <Heading size="large">
              {workerId
                ? `Audit History: ${workerName}`
                : 'System Audit History'}
            </Heading>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </Stack>

          {/* Filters */}
          <Card title="Filters">
            <Card.Section>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '12px',
                }}
              >
                <div>
                  <Body size="small" weight="medium">
                    Action Type:
                  </Body>
                  <select
                    value={filters.action_type}
                    onChange={(e) =>
                      handleFilterChange('action_type', e.target.value)
                    }
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #e1e5e9',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  >
                    <option value="">All Actions</option>
                    <option value="bypass">Bypass</option>
                    <option value="state_change">State Change</option>
                    <option value="manual_completion">Manual Completion</option>
                  </select>
                </div>
                <div>
                  <Body size="small" weight="medium">
                    Date From:
                  </Body>
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) =>
                      handleFilterChange('date_from', e.target.value)
                    }
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #e1e5e9',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                </div>
                <div>
                  <Body size="small" weight="medium">
                    Date To:
                  </Body>
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) =>
                      handleFilterChange('date_to', e.target.value)
                    }
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #e1e5e9',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                </div>
              </div>
              <Stack gap={8} style={{ marginTop: '12px' }}>
                <button
                  onClick={applyFilters}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Apply Filters
                </button>
                <button
                  onClick={clearFilters}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Clear
                </button>
              </Stack>
            </Card.Section>
          </Card>

          {/* Audit Records */}
          <Card title={`Audit Records (${pagination.total_count} total)`}>
            <Card.Section>
              {loading ? (
                <Stack
                  align="center"
                  justify="center"
                  style={{ padding: '40px 0' }}
                >
                  <LoadingState
                    overlayColor="white"
                    text="Loading audit history..."
                  />
                </Stack>
              ) : auditHistory.length > 0 ? (
                <Stack gap={12} vertical>
                  {auditHistory.map((audit) => (
                    <div
                      key={audit.id}
                      style={{
                        padding: '16px',
                        border: '1px solid #e1e5e9',
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                      }}
                    >
                      <Stack gap={12} vertical>
                        <Stack gap={12} justify="space-between" align="center">
                          <Stack gap={8} align="center">
                            {getActionTypeChip(audit.action_type)}
                            <Body weight="medium">
                              {audit.admin_user.name} ({audit.admin_user.email})
                            </Body>
                          </Stack>
                          <Body size="small" color="lighter">
                            {formatDate(audit.created_at)}
                          </Body>
                        </Stack>

                        {!workerId && (
                          <Body>
                            <strong>Worker:</strong> {audit.worker.name} (
                            {audit.worker.email})
                          </Body>
                        )}

                        <Stack gap={16}>
                          <Stack gap={4} vertical>
                            <Body size="small" weight="medium">
                              Previous State:
                            </Body>
                            <Body size="small">
                              {audit.previous_state || 'N/A'}
                            </Body>
                          </Stack>
                          <Stack gap={4} vertical>
                            <Body size="small" weight="medium">
                              New State:
                            </Body>
                            <Body size="small">{audit.new_state}</Body>
                          </Stack>
                        </Stack>

                        {audit.reason && (
                          <div>
                            <Body size="small" weight="medium">
                              Reason:
                            </Body>
                            <Body size="small" style={{ marginTop: '4px' }}>
                              {audit.reason}
                            </Body>
                          </div>
                        )}

                        {audit.metadata &&
                          Object.keys(audit.metadata).length > 0 && (
                            <div>
                              <Body size="small" weight="medium">
                                Additional Details:
                              </Body>
                              <div style={{ marginTop: '4px' }}>
                                {Object.entries(audit.metadata).map(
                                  ([key, value]) => (
                                    <Body
                                      key={key}
                                      size="small"
                                      color="lighter"
                                    >
                                      {key}: {String(value)}
                                    </Body>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                      </Stack>
                    </div>
                  ))}
                </Stack>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <Body color="lighter">No audit records found</Body>
                </div>
              )}
            </Card.Section>
          </Card>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <Stack gap={8} justify="center" align="center">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page <= 1}
                style={{
                  padding: '6px 12px',
                  backgroundColor:
                    pagination.current_page <= 1 ? '#ccc' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor:
                    pagination.current_page <= 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                }}
              >
                Previous
              </button>

              <Body size="small">
                Page {pagination.current_page} of {pagination.total_pages}
              </Body>

              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page >= pagination.total_pages}
                style={{
                  padding: '6px 12px',
                  backgroundColor:
                    pagination.current_page >= pagination.total_pages
                      ? '#ccc'
                      : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor:
                    pagination.current_page >= pagination.total_pages
                      ? 'not-allowed'
                      : 'pointer',
                  fontSize: '14px',
                }}
              >
                Next
              </button>
            </Stack>
          )}
        </Stack>
      </div>
    </div>
  );
};

export default AuditHistoryModal;
